/* Split-lines reveal — the line-by-line entrance used across
   wecanflyagency.com, without GSAP/SplitText.

   Wraps every word, groups words that share a baseline into a line, and lets
   CSS slide each line out of its own overflow-hidden box. Re-splits on resize
   and after webfonts land, because both change where the lines break.

   Markup: <h2 data-split="lines">…</h2>
   Optional: data-split-delay="80" (ms between lines, default 100) */
(function () {
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var items = [];
  var resizeTimer = null;
  var lastWidth = window.innerWidth;

  // Text blocks inside the host (paragraphs, list items, headings). Splitting
  // each one separately keeps the original structure — a <p> replaced by bare
  // lines would lose its margins and pick up sibling spacing from .rte.
  function targetsOf(host) {
    var blocks = host.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, blockquote');
    return blocks.length ? Array.prototype.slice.call(blocks) : [host];
  }

  function splitBlock(el, startIndex, delay) {
    var original = el.dataset.splitOriginal;
    if (original === undefined) {
      original = el.innerHTML;
      el.dataset.splitOriginal = original;
    } else {
      el.innerHTML = original;
    }

    // Wrap each word of every text node, leaving inline markup intact.
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    textNodes.forEach(function (textNode) {
      var frag = document.createDocumentFragment();
      textNode.nodeValue.split(/(\s+)/).forEach(function (chunk) {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) {
          frag.appendChild(document.createTextNode(chunk));
          return;
        }
        var word = document.createElement('span');
        word.className = 'split-word';
        word.textContent = chunk;
        frag.appendChild(word);
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });

    // Group words into lines by their vertical offset.
    var words = Array.prototype.slice.call(el.querySelectorAll('.split-word'));
    if (!words.length) return 0;

    var lines = [];
    var currentTop = null;
    words.forEach(function (word) {
      var top = word.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        currentTop = top;
        lines.push([]);
      }
      lines[lines.length - 1].push(word);
    });

    var fragment = document.createDocumentFragment();

    lines.forEach(function (lineWords, index) {
      var line = document.createElement('span');
      line.className = 'split-line';
      var inner = document.createElement('span');
      inner.className = 'split-line__inner';
      inner.style.transitionDelay = (startIndex + index) * delay + 'ms';

      lineWords.forEach(function (word, i) {
        if (i > 0) inner.appendChild(document.createTextNode(' '));
        inner.appendChild(word.cloneNode(true));
      });

      line.appendChild(inner);
      fragment.appendChild(line);
    });

    el.innerHTML = '';
    el.appendChild(fragment);
    return lines.length;
  }

  function splitHost(host) {
    var delay = parseInt(host.getAttribute('data-split-delay') || '100', 10);
    var index = 0;
    targetsOf(host).forEach(function (target) {
      index += splitBlock(target, index, delay);
    });
    host.classList.add('is-split');
  }


  /* ---------------------------------------------------------------------
     Chars mode — the scrubbed reveal from wecanflyagency.com: characters
     fade in one after another, tied to scroll position rather than time.
     JS writes a single --split-progress per element; CSS derives every
     character's opacity from it, so a long heading costs one style write.
     --------------------------------------------------------------------- */
  var scrubItems = [];
  var scrubTicking = false;
  var scrubBound = false;

  function splitChars(host) {
    var original = host.dataset.splitOriginal;
    if (original === undefined) {
      original = host.innerHTML;
      host.dataset.splitOriginal = original;
    } else {
      host.innerHTML = original;
    }

    var walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    textNodes.forEach(function (textNode) {
      var frag = document.createDocumentFragment();
      textNode.nodeValue.split(/(\s+)/).forEach(function (chunk) {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) {
          frag.appendChild(document.createTextNode(chunk));
          return;
        }
        // Words stay unbreakable so the scrub never reflows mid-animation.
        var word = document.createElement('span');
        word.className = 'split-word';
        chunk.split('').forEach(function (ch) {
          var span = document.createElement('span');
          span.className = 'split-char';
          span.textContent = ch;
          word.appendChild(span);
        });
        frag.appendChild(word);
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });

    // Timing copied from their GSAP tween: gsap.to(chars, {opacity: 1,
    // stagger: 0.05, ease: 'none'}) with the default 0.5s duration. Character
    // i starts at i*0.05 on a timeline lasting 0.5 + 0.05*(n-1), so its share
    // of the scrubbed range is fixed rather than a guessed constant.
    var chars = host.querySelectorAll('.split-char');
    var STAGGER = 0.05;
    var DURATION = 0.5;
    var total = DURATION + STAGGER * (chars.length - 1);
    var window_ = DURATION / total;

    chars.forEach(function (ch, i) {
      ch.style.setProperty('--char-start', ((i * STAGGER) / total).toFixed(4));
    });
    host.style.setProperty('--char-window', window_.toFixed(4));
    host.dataset.splitDuration = total;
    host.classList.add('is-split');
  }

  // The scrubbed timeline is paced by scrolling, but played back in time it
  // feels sluggish on a long heading (47 characters would take 2.8s), so the
  // in-time playback runs the same timeline compressed.
  var TIME_SCALE = 0.55;

  function playInTime(el) {
    var duration = parseFloat(el.dataset.splitDuration || '1') * 1000 * TIME_SCALE;
    var started = null;
    function step(now) {
      if (started === null) started = now;
      var p = Math.min(1, (now - started) / duration);
      el.style.setProperty('--split-progress', p.toFixed(4));
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  // Scroll range the reveal is spread over, as a share of the viewport height.
  // wecanflyagency.com uses top 80% -> top 10%, i.e. 0.7vh of scrolling, which
  // reads as sluggish here: a heading only finishes once it is almost off the
  // top. Finishing at 45% completes it around mid-screen, in 0.4vh of scroll.
  var SCRUB_START = 0.85;
  var SCRUB_END = 0.45;

  function updateScrub() {
    var vh = window.innerHeight;
    var start = vh * SCRUB_START;
    var end = vh * SCRUB_END;
    scrubItems.forEach(function (el) {
      var top = el.getBoundingClientRect().top;
      var progress = (start - top) / (start - end);
      progress = Math.max(0, Math.min(1, progress));
      el.style.setProperty('--split-progress', progress.toFixed(4));
    });
  }

  function onScrubScroll() {
    if (scrubTicking) return;
    scrubTicking = true;
    requestAnimationFrame(function () {
      updateScrub();
      scrubTicking = false;
    });
  }

  function initChars(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = Array.prototype.slice.call(scope.querySelectorAll('[data-split="chars"]'));
    if (!els.length) return;

    els.forEach(function (el) {
      if (el.dataset.splitInit === 'true') return;
      el.dataset.splitInit = 'true';

      if (reduced) {
        el.classList.add('is-split');
        el.style.setProperty('--split-progress', '1');
        return;
      }

      splitChars(el);

      // A heading sitting near the top of the document can never reach the
      // end of the scrubbed range — there is no scroll left to give it — so
      // it would stay half-revealed forever. Play those on a timeline, the
      // same way their [Fade] elements resolve above the fold.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.8) {
        playInTime(el);
        return;
      }

      scrubItems.push(el);
    });

    // Bind once. This used to test scrubItems.length === 1 after the loop,
    // so a page with more than one scrubbed element never got a listener and
    // every heading past the first stayed invisible.
    if (!scrubBound) {
      scrubBound = true;
      window.addEventListener('scroll', onScrubScroll, { passive: true });
      window.addEventListener('resize', onScrubScroll);
    }
    updateScrub();
  }

  function reveal(el) {
    // The lines were just inserted; without a forced reflow the browser can
    // collapse "insert + add class" into one style pass and skip the transition.
    void el.offsetWidth;
    el.classList.add('is-revealed');
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }

  function observe(el) {
    // Anything already on screen (the hero, above all) is revealed straight
    // away. IntersectionObserver does not report while a tab is in the
    // background, which would otherwise leave the first screen blank.
    if (!('IntersectionObserver' in window) || inViewport(el)) {
      requestAnimationFrame(function () { reveal(el); });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(el);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = Array.prototype.slice.call(scope.querySelectorAll('[data-split="lines"]'));
    if (!els.length) return;

    els.forEach(function (el) {
      if (el.dataset.splitInit === 'true') return;
      el.dataset.splitInit = 'true';

      if (reduced) {
        el.classList.add('is-split', 'is-revealed');
        return;
      }

      splitHost(el);
      items.push(el);
      observe(el);
    });
  }

  function resplit() {
    // Only width changes matter; mobile browsers fire resize on scroll.
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    items.forEach(function (el) {
      var revealed = el.classList.contains('is-revealed');
      splitHost(el);
      if (revealed) el.classList.add('is-revealed');
    });
  }

  function start() {
    // Line breaks depend on font metrics, so the split has to wait for the
    // webfont. Splitting first and re-splitting after fonts.ready would rebuild
    // the lines mid-animation and drop it — on a shop the font comes from a CDN,
    // so that race is the normal case, not an edge case.
    var started = false;

    function go() {
      if (started) return;
      started = true;
      init();
      initChars();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
      // A font that never resolves must not leave the text hidden.
      setTimeout(go, 1200);
    } else {
      go();
    }

    // Last-resort guard: whatever happens, nothing stays invisible.
    setTimeout(function () {
      document.querySelectorAll('[data-split]:not(.is-revealed)').forEach(function (el) {
        el.classList.add('is-split', 'is-revealed');
        if (el.getAttribute('data-split') === 'chars') {
          el.style.setProperty('--split-progress', '1');
        }
      });
    }, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resplit, 200);
  });

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    initChars(e.target);
  });
})();
